<template>
  <SupportBanner
    v-if="supportBanner && !isWebview"
    v-bind="supportBanner"
    :cms-page-id="data?.id"
  />

  <!-- name is a reserved NuxtPage prop https://nuxt.com/docs/api/components/nuxt-page#props -->
  <NuxtPage
    v-if="data && isSpecificLayout"
    v-bind="omit(data, ['name'])"
    data-test="specific-layout"
  />

  <DefaultLayout
    v-if="data && !isSpecificLayout"
    v-bind="data"
    data-test="default-layout"
    :isWebview
    :simpleLayout
  />
</template>

<script lang="ts" setup>
import SupportBanner from '@backmarket/nuxt-layer-cms/SupportBanner.vue'
import { useIsWebview } from '@backmarket/nuxt-module-navigation/useIsWebview'
import { omit } from '@backmarket/utils/object/omit'

import { useCMSHead } from './composables/useCMSHead'
import { useExperienceLayerIsEnabled } from './composables/useExperienceLayerIsEnabled'
import { useFetchCMSPageContent } from './composables/useFetchCMSPageContent'
import { useFetchFallbackOnError } from './composables/useFetchFallbackOnError'
import { useFetchPageSupportBanner } from './composables/useFetchPageSupportBanner'
import { useHandleFetchError } from './composables/useHandleFetchError'
import { useHandleIncorrectRouteParams } from './composables/useHandleIncorrectRouteParams'
import { useIsSpecificLayout } from './composables/useIsSpecificLayout'
import { usePreloadFirstCarouselImage } from './composables/usePreloadFirstCarouselImage'
import DefaultLayout from './layout/Default.vue'

const { simpleLayout = false } = defineProps<{ simpleLayout?: boolean }>()

const fetchFallbackOnError = useFetchFallbackOnError()

const experienceLayerIsEnabled = useExperienceLayerIsEnabled()
const { data, error } = await useFetchCMSPageContent({
  fetchFallbackOnError,
  experienceLayerIsEnabled,
})

usePreloadFirstCarouselImage(data)
useHandleFetchError(error)
useHandleIncorrectRouteParams(data)
useCMSHead(data)
const isSpecificLayout = useIsSpecificLayout(data)
const supportBanner = useFetchPageSupportBanner(data.value?.blockErrors)

const isWebview = useIsWebview()
</script>
